import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

// Components
import Pagination from '../UI/Pagination/Pagination';
import Loader from '../UI/Loader/Loader';
import AddUser from './AddUser';

// Hooks
import { useSearch } from '../../hooks/useSearch';
import AuthContext from '../../store/auth-context';

// Services
import { getModuleList, getOrganisation } from '../../services/notification.services';
import { fetchUsers, deleteUser as deleteUserApi, fetchUserDetail, switchUser as switchUserApi } from '../../services/user.service';

// Utils
import { parseJwtToken } from '../../utils/jwt.utils';

// Types
import { User, UserPermission, Module, UserData } from '../../types/user.types';

// Constants
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  USER_TYPES,
  TOAST_MESSAGES,
  STORAGE_KEYS,
} from './constants';

// Styles
import style from './OrgSetting.module.css';
import tableStyle from '../Style/TableModel.module.css';
import fontSize from '../Style/FontSize.module.css';
import common from '../Style/common.module.css';
import button from '../Style/ButtonStyle.module.css';
import font from '../Style/FontFamily.module.css';
import '../Style/Table-style.css';

interface UsersProps {
  sCode?: string;
  orgUuid?: any;
  setSelectedCode?: (code: string) => void;
  setKey?: (key: string) => void;
}

const renderTooltip = (props: any) => (
  <Tooltip id="button-tooltip" {...props}>
    Click to Impersonate
  </Tooltip>
);

const Users: React.FC<UsersProps> = ({ 
  sCode, 
  orgUuid, 
  setSelectedCode = () => {}, 
  setKey = () => {} 
}) => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();

  // State
  const [userList, setUserList] = useState<User[]>([]);
  const [moduleList, setModuleList] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [organisationName, setOrganisationName] = useState('');
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [userDetail, setUserDetail] = useState<User | null>(null);
  const [userDetailPermission, setUserDetailPermission] = useState<UserPermission[]>([]);

  // Search hook
  const { searchText, setSearchText, filteredItems } = useSearch<User>({
    items: userList,
    searchFn: (user, query) => user.email.toLowerCase().includes(query),
  });

  // Pagination calculations
  const firstPageIndex = (currentPage - 1) * pageSize;
  const lastPageIndex = firstPageIndex + pageSize;
  const paginatedUsers = filteredItems.slice(firstPageIndex, lastPageIndex);

  // Load users
  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgDetail = sCode?.split('&&&') || [];
      const orgId = orgDetail[0] || '';
      
      if (orgDetail.length > 0) {
        setOrganisationName(orgDetail[1]);
      }

      const users = await fetchUsers(
        authCtx.token,
        authCtx.userData.user_id.toString(),
        orgId,
        authCtx.userData.usertype
      );
      
      setUserList(users);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error(TOAST_MESSAGES.ERROR_LOADING_USERS);
      setUserList([]);
    } finally {
      setIsLoading(false);
    }
  }, [authCtx.token, authCtx.userData, sCode]);

  // Load modules
  useEffect(() => {
    // Guard: Don't make API calls if token is being cleared (logout in progress)
    if (!authCtx.token || !authCtx.isLoggedIn) {
      return;
    }
    
    getModuleList(authCtx.token)
      .then((resp) => setModuleList(Array.isArray(resp) ? resp : (resp as any)?.moduleList || []))
      .catch((err) => console.error('Error loading modules:', err));
  }, [authCtx.token, authCtx.isLoggedIn]);

  // Load users on mount and when dependencies change
  useEffect(() => {
    // Guard: Don't load users if token is being cleared (logout in progress)
    if (!authCtx.token || !authCtx.isLoggedIn) {
      return;
    }
    
    loadUsers();
  }, [loadUsers, authCtx.token, authCtx.isLoggedIn]);

  // Handlers
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setCurrentPage(1);
  };

  const toggleUserModal = () => {
    setIsUserModalOpen((prev) => !prev);
    if (isUserModalOpen) {
      setUserDetail(null);
      setUserDetailPermission([]);
    }
  };

  const handleEditUser = async (uuid: string) => {
    // Guard: Don't make API calls if token is being cleared (logout in progress)
    if (!authCtx.token || !authCtx.isLoggedIn) {
      return;
    }
    
    try {
      setIsLoading(true);
      const data = await fetchUserDetail(authCtx.token, uuid);
      setUserDetail(data.user);
      setUserDetailPermission(data.userPermission);
      toggleUserModal();
    } catch (error) {
      console.error('Error fetching user detail:', error);
      toast.error('Failed to load user details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await deleteUserApi(authCtx.token, userToDelete.id);
      
      const message = response.status === 302 ? response.error : response.msg;
      toast.success(message || TOAST_MESSAGES.USER_DELETED_SUCCESS);
      
      setUserList((prev) => prev.filter((user) => user.id !== userToDelete.id));
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(TOAST_MESSAGES.ERROR_DELETING_USER);
    }
  };

  const handleSwitchUser = async (uuid: string) => {
    // Validate required parameters
    if (!authCtx.token || authCtx.token.trim() === '') {
      toast.error('Authentication token is missing. Please log in again.');
      return;
    }

    if (!uuid || uuid.trim() === '') {
      toast.error('User ID is missing. Cannot switch user.');
      return;
    }

    try {
      // Save super admin credentials
      if (!localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_ID)) {
        localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_ID, authCtx.userData.user_id.toString());
        localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_TOKEN, authCtx.token);
        localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_NAME, authCtx.userData.name);
        localStorage.setItem(STORAGE_KEYS.SUPER_ADMIN_USERTYPE, authCtx.userData.usertype);
      }

      setIsLoading(true);
      const data = await switchUserApi(authCtx.token, uuid);
      const udata = data.response;

      if (!udata.token) {
        throw new Error('No token received');
      }

      const tokenUserData = parseJwtToken(udata.token);
      const orgId = tokenUserData.user.org;

      // Fetch organization details
      const orgDetail = await getOrganisation(orgId, udata.token);

      if (orgDetail.status === 400) {
        throw new Error('Could not get organisation this user belongs to');
      }

      const org = orgDetail;
      const superAdmin = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_ID);
      const superAdminName = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_NAME);
      const superAdminToken = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_TOKEN);
      const storedUsertype = localStorage.getItem(STORAGE_KEYS.SUPER_ADMIN_USERTYPE);
      const superAdminUsertype = (storedUsertype === 'INTERNALADMIN' ? 'INTERNALADMIN' : 'OWNER') as 'OWNER' | 'INTERNALADMIN';

      const userData: UserData = {
        uuid: udata.userId,
        superAdminId: superAdmin || undefined,
        superAdminName: superAdminName || undefined,
        superAdminToken: superAdminToken || undefined,
        user_id: udata.user_id,
        name: `${udata.firstName} ${udata.lastName}`,
        uid: tokenUserData.user.id,
        usertype: 'INTERNALADMIN' as UserData['usertype'], // Switched users are always INTERNALADMIN
        orgName: org.name,
        orgLogoUrl: org.logoUrl,
        token: udata.token,
        refreshToken: udata.refreshToken,
        orgId,
        epubOrgId: org.epubOrgId,
        orgWisitiaID: org.org_wistia_project_id,
        thumbImage: udata.thumbImage,
        CHANNEL_PUSH_NOTIFICATION: org.CHANNEL_PUSH_NOTIFICATION,
        EDITOR_ATTACHMENT: org.EDITOR_ATTACHMENT,
        EDITOR_EXTERNAL_LINK: org.EDITOR_EXTERNAL_LINK,
        EDITOR_LINK: org.EDITOR_LINK,
        EDITOR_TEXT: org.EDITOR_TEXT,
        EDIT_EMOJI: org.EDIT_EMOJI,
        EDIT_EPUB: org.EDIT_EPUB,
      };
      debugger;
      authCtx.login(userData);
      navigate('/notifications');
    } catch (error) {
      console.error('Error switching user:', error);
      toast.error(TOAST_MESSAGES.ERROR_SWITCHING_USER);
    } finally {
      setIsLoading(false);
    }
  };

  const goBackToOrganisation = () => {
    navigate('/Organisation/#users');
    setSelectedCode('');
    setKey('third');
  };

  // Escape key handler for modal
  const handleEscKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && userToDelete) {
        setUserToDelete(null);
      }
    },
    [userToDelete]
  );

  useEffect(() => {
    document.addEventListener('keyup', handleEscKey);
    return () => document.removeEventListener('keyup', handleEscKey);
  }, [handleEscKey]);

  return (
    <>
      {isLoading && <Loader />}

      {isUserModalOpen && (
        <AddUser
          modalStatus={isUserModalOpen}
          onAction={toggleUserModal}
          setUserList={setUserList}
          userList={userList}
          setMessage={() => {}}
          userDetail={userDetail}
          userDetailPermission={userDetailPermission}
          moduleList={moduleList}
        />
      )}

      <div className={style.creatUserArea}>
        <div className={style.settingbutton}>
          <button
            onClick={toggleUserModal}
            className={`${button.iconBtn} ${button.modalBtn} ${button.redButton}`}
          >
            <i className="icon icon-plus-circle"></i> New Content Creator
          </button>
        </div>
        <div className={style.tabs}>
          <div className={`form-group ${tableStyle.hassearch}`}>
            <label htmlFor="SearchInput" className="hidenText">
              Search
            </label>
            <input
              value={searchText}
              id="SearchInput"
              onChange={handleSearchChange}
              className={tableStyle.searchInput}
              type="search"
              placeholder="Search by email..."
            />
            <i className={`icon icon-search2 ${tableStyle.searchIcon}`}></i>
          </div>
        </div>
      </div>

      {organisationName && (
        <div className="selectOrg">
          <p>Organisation {organisationName}</p>
          <button
            type="button"
            className={style.backBtn}
            onClick={goBackToOrganisation}
          >
            Go Back
          </button>
        </div>
      )}

      <div className="table-design">
        <table className={`table table-hover ${style.categorytable}`}>
          <thead className={style.heading}>
            <tr>
              <th scope="col">First Name</th>
              <th scope="col">Last Name</th>
              <th scope="col">Email Address</th>
              {authCtx.userData.usertype !== USER_TYPES.INTERNAL_ADMIN && <th scope="col">Org Name</th>}
              <th scope="col">User Type</th>
              <th scope="col">Status</th>
              <th scope="col">Phone</th>
              <th scope="col">Action</th>
            </tr>
          </thead>
          <tbody className={style.tablebody}>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user) => (
                <tr key={user.id}>
                  <td>{user.firstName}</td>
                  <td>{user.lastName}</td>
                  <td>
                    {!authCtx.userData.orgId ? (
                      <OverlayTrigger
                        placement="right"
                        delay={{ show: 50, hide: 400 }}
                        overlay={renderTooltip}
                      >
                        <button
                          type="button"
                          className={style.selectUser}
                          onClick={() => handleSwitchUser(user.uuid)}
                        >
                          {user.email}
                        </button>
                      </OverlayTrigger>
                    ) : (
                      user.email
                    )}
                  </td>
                  {authCtx.userData.usertype !== USER_TYPES.INTERNAL_ADMIN && (
                    <td>
                      {authCtx.userData.usertype === USER_TYPES.OWNER
                        ? user.orgName
                        : authCtx.userData.orgName}
                    </td>
                  )}
                  <td>{user.usertype}</td>
                  <td>
                    <i className={`fa fa-check ${style.statusActive}`}></i>
                  </td>
                  <td>{user.phoneNumber}</td>
                  <td>
                    {(authCtx.userData.usertype === USER_TYPES.OWNER || authCtx.userData.usertype === 'INTERNALADMIN') && (
                      <span className="actionStyleButton impersonateBtn">
                        <i className="icon icon-key" onClick={() => handleSwitchUser(user.uuid)}></i>
                      </span>
                    )}
                    <span className="actionStyleButton">
                      <button type="button" title="Edit" onClick={() => handleEditUser(user.uuid)}>
                        <i className="icon icon-pencil"></i>
                      </button>
                    </span>
                    <span className="actionStyleButton">
                      <button type="button" title="Delete" onClick={() => setUserToDelete(user)}>
                        <i className="fa fa-trash-o"></i>
                      </button>
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className={style.noRecordCell}>
                  <div className={style.loader}>
                    <div className={style['text-loader']}>
                      {searchText ? 'No users found matching your search' : 'No record found'}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className={`row justify-content-between ${tableStyle.listShow}`}>
          {userList.length > DEFAULT_PAGE_SIZE && (
            <div className="col-4">
              <label className={tableStyle.listLabel}>
                Show
                <select
                  className={`form-select ${tableStyle.listSelect}`}
                  value={pageSize}
                  onChange={handlePageSizeChange}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <div className="col-4">
            <div className={tableStyle.paginationContainer}>
              <Pagination
                currentPage={currentPage}
                totalCount={filteredItems.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={!!userToDelete}
        className="alertBgContainer"
        contentLabel="Delete User Confirmation"
      >
        <div className="alertBox">
          <h4 className={tableStyle.alertTitle}>Alert</h4>
          <p className={`${fontSize.smSize} ${font.fontRegular} ${common.pTag}`}>
            Are you sure want to <b className={font.fontBold}>delete this user?</b>
          </p>
          <div className="bottomArea">
            <button
              onClick={() => setUserToDelete(null)}
              className={`${button.smBtn} ${button.lightBrownButton}`}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteUser}
              className={`${button.smBtn} ${button.blueButton} ${button.buttonSpaceLeft}`}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Users;
